import { hasYesOrNo } from 'models/validate';
import { DEFAULT_LATEST } from 'constants/dateLimits';
import { YES } from 'constants/values';
import { LocationModel, ValidateJSModel, ValidateJSOptions } from 'forms/shared/types';
import { getSafeDateTime } from 'helpers/date';
import { isPVQForm } from 'helpers/form';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { DeepSafePartial } from 'types/types';
import { isSafeUnitedStates } from 'helpers/locations';
import { LegalSentenceModel } from './legalSentence';
import { DateRangeBaseValidatorOptions } from '../../../../shared/validators/base/dateRange/types';

function makeDateRangeOptions(options: ValidateJSOptions): DateRangeBaseValidatorOptions {
  const applicantBirthdate = getSafeDateTime(options.applicantBirthdate);
  const offenseDate = getSafeDateTime(options.offenseDate);
  let earliestDate;
  let earlyError;

  if (offenseDate.valid) {
    earliestDate = offenseDate.datetime;
    earlyError = 'error.beforeOffense';
  } else {
    earliestDate = applicantBirthdate.datetime;
    earlyError = 'error.applicantMin';
  }

  return {
    earliest: earliestDate,
    latest: DEFAULT_LATEST,
    tooEarly: earlyError,
    requireYear: false,
  };
}

const legalSentenceValidator: ValidateJSModel<LegalSentenceModel> = {
  IncarcerationFacility: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && options.sentenceJudgmentValues?.includes?.('Imprisonment')) {
      return { hasValue: true };
    }

    return {};
  },
  IncarcerationFacilityAddress: (value: DeepSafePartial<LocationModel | undefined>, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && options.sentenceJudgmentValues?.includes?.('Imprisonment')) {
      return {
        location: {
          requireStreet: isSafeUnitedStates(value),
          validator: pvqLocationModelValidator,
        },
        presence: true,
      };
    }

    return {};
  },
  GrantedParole: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && options.sentenceJudgmentValues?.includes?.('Imprisonment')) {
      return { hasValue: { validator: hasYesOrNo } };
    }

    return {};
  },
  GrantedParoleDates: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)
      && options.sentenceJudgmentValues?.includes?.('Imprisonment')
      && attributes?.GrantedParole?.value === 'Yes') {
      return {
        daterange: makeDateRangeOptions(options),
        presence: true,
      };
    }

    return {};
  },
  // Validation is skipped for both PVQ and non-PVQ
  IncarcerationDatesNA: {},
  // Validation is skipped for both PVQ and non-PVQ
  ProbationDatesNA: {},
  Description: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    return { presence: true, hasValue: true };
  },
  ExceedsYear: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    if (options?.requireLegalOffenseSentenced) {
      if (attributes?.Incarcerated?.value === 'Yes') {
        return {
          presence: true,
          hasValue: {
            validator: {
              inclusion: { within: [YES] },
            }
          },
        };
      }
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo },
      };
    }
    return {};
  },
  Incarcerated: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }

    /*
    This is based of the eQip processing by months. This is not accurate for the following scenario
    If a person was sentenced to over a year of prison the answer is YES to "Were you sentenced to imprisonment for a term exceeding 1 year?"
    The if that person was released early before a year was up the answer is NO to 'Were you incarcerated as a result of that sentence for not less than 1 year?"
    The dates of imprisonment might have been Jan 31th, 2007 to Jan 1st, 2008 which is incarcerated for less than a year but since the system only does month and year
    the month handling by eQip does not account for that. eQip indicates this is a 13 month incarceration but indeed a less than 12 month incarceration.
    Help tips are added to aid the users.
    */
    if (options?.requireLegalOffenseIncarcerated) {
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo },
      };
    }
    return {};
  },
  IncarcerationDates: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (options.sentenceJudgmentValues?.includes?.('Imprisonment')) {
        return {
          daterange: makeDateRangeOptions(options),
          presence: true,
        };
      }

      return {};
    }

    if (attributes?.IncarcerationDatesNA?.applicable === false && attributes.Incarcerated?.value !== 'Yes') {
      return {};
    }

    const daterangeOptions: DateRangeBaseValidatorOptions = makeDateRangeOptions(options);

    // Calculating using months to match eQip handling. This is not an accurate measurement for years in prison.
    if (attributes?.Incarcerated?.value === 'Yes') {
      daterangeOptions.minDuration = { months: 12 };
    } else if (attributes?.Incarcerated?.value === 'No') {
      daterangeOptions.maxDuration = { months: 12 };
    }

    return {
      presence: true,
      daterange: daterangeOptions,
    };
  },
  ProbationDates: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (options.sentenceJudgmentValues?.includes('Probation')) {
        return {
          daterange: makeDateRangeOptions(options),
          presence: true,
        };
      }

      return {};
    }

    if (attributes?.ProbationDatesNA?.applicable === false) {
      return {};
    }

    return {
      presence: true,
      daterange: makeDateRangeOptions(options),
    };
  },
};

export default legalSentenceValidator;
